/**
 * ModelLoader — carrega e CACHEIA GLBs (GLTFLoader) e instancia cópias skinned com
 * SkeletonUtils.clone(). Regra de ouro (§12): NUNCA lança / nunca quebra o boot. Se um
 * GLB falta ou falha ao decodificar, a entidade cai no procedural (o `instantiate` devolve
 * null e loga um warning só em DEV).
 *
 * Offline (§14): resolve o path contra `document.baseURI`, então os GLBs vêm de `public/`
 * (copiado para `dist/`) — sem CDN, sem rede; funciona no APK Capacitor em modo avião.
 * Um GLB é carregado UMA vez e reusado por todos os spawns (sem reload por instância, §10).
 */
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { clone as skeletonClone } from "three/examples/jsm/utils/SkeletonUtils.js";
import type { ModelEntry } from "./ModelRegistry";

export type LoadStatus = "loaded" | "missing" | "error" | "disabled" | "pending";

export interface LoadedModel {
  readonly scene: THREE.Group;
  readonly clips: THREE.AnimationClip[];
  readonly triangles: number;
  readonly materials: number;
}

export interface ModelInstance {
  readonly root: THREE.Object3D; // clone pronto para adicionar à cena
  readonly clips: THREE.AnimationClip[]; // clips compartilhados (o Mixer casa por nome)
}

export interface EntryStatus {
  readonly key: string;
  readonly path: string;
  readonly status: LoadStatus;
  readonly triangles?: number;
  readonly materials?: number;
  readonly clipNames?: string[];
  readonly detail?: string;
}

function isDev(): boolean {
  try {
    return !!import.meta.env && import.meta.env.DEV;
  } catch {
    return false;
  }
}

/** Resolve o path do asset para uma URL absoluta relativa ao documento (offline-safe). */
function resolveUrl(assetPath: string): string {
  try {
    const base = typeof document !== "undefined" ? document.baseURI : "http://localhost/";
    return new URL(assetPath, base).href;
  } catch {
    return assetPath;
  }
}

/** Nomes de todos os nós do grafo (para casar tracks de animação com bones). */
function collectNodeNames(root: THREE.Object3D): Set<string> {
  const names = new Set<string>();
  root.traverse((o) => {
    if (o.name) names.add(o.name);
  });
  return names;
}

/** Nome do nó-alvo de um track ("hand.R.quaternion" → "hand.R"; usa o último ponto). */
function trackNodeName(trackName: string): string {
  const i = trackName.lastIndexOf(".");
  return i >= 0 ? trackName.slice(0, i) : trackName;
}

/** Os clips referenciam algum bone existente no esqueleto? (compat parcial já basta). */
function clipsMatchSkeleton(clips: THREE.AnimationClip[], boneNames: Set<string>): boolean {
  for (const clip of clips) {
    for (const t of clip.tracks) {
      if (boneNames.has(trackNodeName(t.name))) return true;
    }
  }
  return false;
}

function countModel(scene: THREE.Object3D): { triangles: number; materials: number } {
  let triangles = 0;
  const mats = new Set<THREE.Material>();
  scene.traverse((o) => {
    const mesh = o as THREE.Mesh;
    if (mesh.isMesh && mesh.geometry) {
      const g = mesh.geometry as THREE.BufferGeometry;
      const idx = g.getIndex();
      const pos = g.getAttribute("position");
      if (idx) triangles += idx.count / 3;
      else if (pos) triangles += pos.count / 3;
      const m = mesh.material;
      if (Array.isArray(m)) m.forEach((x) => mats.add(x));
      else if (m) mats.add(m);
    }
  });
  return { triangles: Math.round(triangles), materials: mats.size };
}

export class ModelLoader {
  private readonly loader = new GLTFLoader();
  private readonly cache = new Map<string, LoadedModel>();
  private readonly statusByKey = new Map<string, EntryStatus>();

  /** Tenta carregar todas as entradas `enabled`. Resolve SEMPRE (falhas viram fallback). */
  async preload(entries: ModelEntry[]): Promise<void> {
    await Promise.all(entries.map((e) => this.loadOne(e)));
  }

  /** Corrida com timeout: um fetch pendurado nunca trava o boot (cai no fallback). */
  private withTimeout<T>(p: Promise<T>, ms: number): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const id = setTimeout(() => reject(new Error("timeout")), ms);
      p.then(
        (v) => {
          clearTimeout(id);
          resolve(v);
        },
        (e) => {
          clearTimeout(id);
          reject(e as Error);
        },
      );
    });
  }

  private async loadOne(entry: ModelEntry): Promise<void> {
    if (!entry.enabled) {
      this.statusByKey.set(entry.key, { key: entry.key, path: entry.assetPath, status: "disabled" });
      return;
    }
    const url = resolveUrl(entry.assetPath);
    try {
      const gltf = await this.withTimeout(this.loader.loadAsync(url), 6000);
      const scene = gltf.scene as THREE.Group;
      const clips = [...(gltf.animations ?? [])];
      // Fontes de animação externas (Quaternius base + Animation Library): mescla os clips,
      // MAS só se o esqueleto for compatível (§9) — senão loga ANIMATION RIG MISMATCH e ignora.
      if (entry.animationSources?.length) {
        const boneNames = collectNodeNames(scene);
        for (const src of entry.animationSources) {
          try {
            const extra = await this.withTimeout(this.loader.loadAsync(resolveUrl(src)), 6000);
            const anims = extra.animations ?? [];
            if (!anims.length) continue;
            if (!clipsMatchSkeleton(anims, boneNames)) {
              console.warn(`[ModelLoader] ANIMATION RIG MISMATCH: ${src} não casa com o esqueleto de ${entry.key} → ignorado (usa clips próprios/fallback).`);
              continue;
            }
            for (const c of anims) clips.push(c);
            if (isDev()) console.info(`[ModelLoader] + ${anims.length} clip(s) de ${src}`);
          } catch {
            /* fonte de animação ausente: ignora (o modelo usa só os clips próprios) */
          }
        }
      }
      const { triangles, materials } = countModel(scene);
      this.cache.set(entry.key, { scene, clips, triangles, materials });
      this.statusByKey.set(entry.key, {
        key: entry.key,
        path: entry.assetPath,
        status: "loaded",
        triangles,
        materials,
        clipNames: clips.map((c) => c.name),
      });
      if (isDev()) console.info(`[ModelLoader] ✓ ${entry.key} (${triangles} tris, ${materials} mat, ${clips.length} clips)`);
    } catch (err) {
      // 404 (asset ausente) é o caso ESPERADO enquanto não há GLB — cai no procedural.
      const missing = String((err as { message?: string })?.message ?? err).match(/404|not found|failed to fetch/i);
      this.statusByKey.set(entry.key, {
        key: entry.key,
        path: entry.assetPath,
        status: missing ? "missing" : "error",
        detail: String((err as { message?: string })?.message ?? err).slice(0, 120),
      });
      if (isDev()) console.warn(`[ModelLoader] ${entry.key}: GLB indisponível (${missing ? "ausente" : "erro"}) → fallback procedural.`);
    }
  }

  isAvailable(key: string): boolean {
    return this.cache.has(key);
  }

  /** Cópia skinned pronta para a cena (SkeletonUtils.clone) + clips compartilhados. */
  instantiate(key: string): ModelInstance | null {
    const loaded = this.cache.get(key);
    if (!loaded) return null;
    try {
      const root = skeletonClone(loaded.scene);
      root.traverse((o) => {
        const mesh = o as THREE.Mesh;
        if (mesh.isMesh) {
          mesh.geometry.userData.shared = true;
          mesh.castShadow = true;
          mesh.frustumCulled = true;
        }
      });
      return { root, clips: loaded.clips };
    } catch (err) {
      if (isDev()) console.warn(`[ModelLoader] clone falhou p/ ${key}:`, err);
      return null;
    }
  }

  loaded(key: string): LoadedModel | null {
    return this.cache.get(key) ?? null;
  }

  statusReport(): EntryStatus[] {
    return [...this.statusByKey.values()];
  }
}

/** Instância única do carregador (cache global de modelos). */
export const modelLoader = new ModelLoader();
